import { useEffect, useMemo, useState } from 'react';
import { Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Skeleton } from '../../ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '../../ui/tabs';
import { addDays, endOfDay, KIND_META, KIND_ORDER } from '../qualityConfig';
import { AssignDetailsPanel } from './AssignDetailsPanel';
import { CatalogPicker, itemKey } from './CatalogPicker';
import { RecruiterPicker } from './RecruiterPicker';
import { SelectedTray } from './SelectedTray';

const defaultForm = () => ({ title: '', review_cycle: 'weekly', call_feedback: '', deadline: endOfDay(addDays(new Date(), 7)) });

const KindTabs = ({ picked }) => (
    <div className="px-4 pt-3">
        <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1">
            {KIND_ORDER.map((k) => {
                const Icon = KIND_META[k].icon;
                const count = picked.filter((i) => i.kind === k).length;
                return (
                    <TabsTrigger key={k} value={k} className="gap-1.5" data-testid={`simulation-tab-${KIND_META[k].testId}`}>
                        <Icon className="h-3.5 w-3.5" /> {KIND_META[k].label}
                        {count > 0 && <span className="rounded-full bg-violet-600 px-1.5 text-[10px] font-semibold text-white">{count}</span>}
                    </TabsTrigger>
                );
            })}
        </TabsList>
    </div>
);

export const AssignTaskDialog = ({ open, onOpenChange, initialUserIds, team, onAssigned }) => {
    const { api } = useAuth();
    const [catalog, setCatalog] = useState(null);
    const [catalogError, setCatalogError] = useState(false);
    const [tab, setTab] = useState('recruiter');
    const [userIds, setUserIds] = useState([]);
    const [selected, setSelected] = useState({});
    const [form, setForm] = useState(defaultForm);
    const [busy, setBusy] = useState(false);
    const initialKey = (initialUserIds || []).join(',');

    useEffect(() => {
        if (!open) return;
        setUserIds(initialKey ? initialKey.split(',') : []);
        setSelected({});
        setForm(defaultForm());
        setTab('recruiter');
    }, [open, initialKey]);

    useEffect(() => {
        if (!open || catalog || catalogError) return;
        api.get('/quality-tasks/admin/catalog').then(({ data }) => setCatalog(data)).catch(() => setCatalogError(true));
    }, [open, catalog, catalogError, api]);

    const picked = useMemo(() => Object.values(selected), [selected]);
    const toggle = (item) => setSelected((s) => { const n = { ...s }; const k = itemKey(item); n[k] ? delete n[k] : (n[k] = item); return n; });
    const setMany = (list, on) => setSelected((s) => { const n = { ...s }; list.forEach((it) => (on ? (n[itemKey(it)] = it) : delete n[itemKey(it)])); return n; });

    const submit = async () => {
        setBusy(true);
        try {
            const { data } = await api.post('/quality-tasks/admin/tasks', {
                user_ids: userIds, items: picked.map(({ kind, ref_key }) => ({ kind, ref_key })),
                deadline: form.deadline.toISOString(), review_cycle: form.review_cycle, title: form.title || null, call_feedback: form.call_feedback,
            });
            toast.success(`Assigned ${picked.length} simulation${picked.length > 1 ? 's' : ''} to ${data.created} recruiter${data.created > 1 ? 's' : ''}`);
            onAssigned();
            onOpenChange(false);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Could not assign the task');
        } finally {
            setBusy(false);
        }
    };

    const canSubmit = userIds.length > 0 && picked.length > 0 && form.deadline && !busy;
    const missing = !userIds.length ? 'Choose at least one recruiter' : !picked.length ? 'Pick at least one simulation' : null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="flex h-[94vh] max-w-6xl flex-col gap-0 overflow-hidden p-0 sm:rounded-2xl" data-testid="assign-task-dialog">
                <DialogHeader className="space-y-1 border-b px-6 py-4 text-left">
                    <DialogTitle className="font-heading text-2xl tracking-tight">Assign Quality Task</DialogTitle>
                    <DialogDescription>Mix simulations from any module, add what you heard on the calls and set a deadline.</DialogDescription>
                    <div className="pt-3"><RecruiterPicker team={team} userIds={userIds} onChange={setUserIds} /></div>
                </DialogHeader>
                <div className="grid min-h-0 flex-1 grid-cols-1 overflow-y-auto lg:grid-cols-12 lg:overflow-hidden">
                    <div className="flex h-[72vh] min-h-0 flex-col border-b lg:col-span-8 lg:h-auto lg:border-b-0 lg:border-r">
                        <Tabs value={tab} onValueChange={setTab} className="flex min-h-0 flex-1 flex-col">
                            <KindTabs picked={picked} />
                            <div className="min-h-0 flex-1">
                                {catalog && <CatalogPicker key={tab} kind={tab} items={catalog.catalog[tab]} selected={selected} onToggle={toggle} onSetMany={setMany} />}
                                {!catalog && !catalogError && <div className="space-y-2 p-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-14 w-full rounded-xl" />)}</div>}
                                {catalogError && (
                                    <div className="flex flex-col items-center gap-3 p-10 text-center text-sm text-muted-foreground" data-testid="assign-catalog-error">
                                        Could not load the simulations.
                                        <Button size="sm" variant="outline" onClick={() => setCatalogError(false)} data-testid="assign-catalog-retry">Try again</Button>
                                    </div>
                                )}
                            </div>
                        </Tabs>
                        <SelectedTray items={picked} onRemove={toggle} onClear={() => setSelected({})} />
                    </div>
                    <div className="bg-muted/20 p-6 lg:col-span-4 lg:overflow-y-auto">
                        <AssignDetailsPanel form={form} setForm={setForm} cycles={catalog?.review_cycles || []} />
                    </div>
                </div>
                <div className="flex flex-col-reverse gap-3 border-t px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-sm text-muted-foreground" data-testid="assign-summary">
                        {missing || `${picked.length} simulation${picked.length > 1 ? 's' : ''} · ${userIds.length} recruiter${userIds.length > 1 ? 's' : ''} · due ${form.deadline.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                    </p>
                    <div className="flex gap-2">
                        <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="assign-cancel-button">Cancel</Button>
                        <Button disabled={!canSubmit} onClick={submit} className="bg-violet-600 text-white hover:bg-violet-700" data-testid="assign-task-submit-button">
                            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                            Assign {picked.length || ''} simulation{picked.length === 1 ? '' : 's'}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};
